import APIService from "./APIService.js";
import { API_BASE_URL } from "../../../config/apiConfig";

// API service for Authentication Failed Attempts functionality
class AuthenticationFailedAttemptsAPI {
  constructor() {
    this.endpoint = "/api/v1/admin/dashboard/auth-failed-attempts";
  }

  /**
   * Get dashboard summary of failed attempts
   * @param {number} hours - Time range in hours (default: 24)
   * @returns {Promise<Object>} Dashboard data
   */
  async getDashboard(hours = 24) {
    try {
      return await APIService.get(`${this.endpoint}/dashboard?hours=${hours}`);
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching dashboard:",
        error
      );
      throw error;
    }
  }

  /**
   * Get recent failed authentication attempts with pagination and optional time filtering
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortBy - Sort field
   * @param {string} sortDir - Sort direction
   * @param {number} hours - Optional time range in hours
   * @returns {Promise<Object>} Paginated failed attempts
   */
  async getFailedAttempts(
    page = 0,
    size = 20,
    sortBy = "attemptTimestamp",
    sortDir = "desc",
    hours = null
  ) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy,
        sortDir,
      });

      if (hours !== null && hours > 0) {
        params.append("hours", hours.toString());
      }

      return await APIService.get(`${this.endpoint}?${params}`);
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching failed attempts:",
        error
      );
      throw error;
    }
  }

  /**
   * Get failed attempts by email address
   * @param {string} email - Email address to search for
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paginated failed attempts for email
   */
  async getAttemptsByEmail(email, page = 0, size = 20) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      return await APIService.get(
        `${this.endpoint}/by-email/${encodeURIComponent(email)}?${params}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching attempts by email:",
        error
      );
      throw error;
    }
  }

  /**
   * Get failed attempts by IP address
   * @param {string} ipAddress - IP address to search for
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paginated failed attempts for IP
   */
  async getAttemptsByIp(ipAddress, page = 0, size = 20) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      return await APIService.get(
        `${this.endpoint}/by-ip/${encodeURIComponent(ipAddress)}?${params}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching attempts by IP:",
        error
      );
      throw error;
    }
  }

  /**
   * Check if an email has exceeded attempt limits
   * @param {string} email - Email address to check
   * @returns {Promise<Object>} Limit status
   */
  async checkEmailLimit(email) {
    try {
      return await APIService.get(
        `${this.endpoint}/check-email-limit/${encodeURIComponent(email)}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error checking email limit:",
        error
      );
      throw error;
    }
  }

  /**
   * Check if an IP address has exceeded attempt limits
   * @param {string} ipAddress - IP address to check
   * @returns {Promise<Object>} Limit status
   */
  async checkIpLimit(ipAddress) {
    try {
      return await APIService.get(
        `${this.endpoint}/check-ip-limit/${encodeURIComponent(ipAddress)}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error checking IP limit:",
        error
      );
      throw error;
    }
  }

  /**
   * Get suspicious IP addresses with high activity
   * @param {number} hours - Time range in hours
   * @returns {Promise<Array>} List of suspicious IPs
   */
  async getSuspiciousIps(hours = 24) {
    try {
      return await APIService.get(
        `${this.endpoint}/suspicious-ips?hours=${hours}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching suspicious IPs:",
        error
      );
      throw error;
    }
  }

  /**
   * Get failure reason statistics
   * @param {number} hours - Time range in hours
   * @returns {Promise<Array>} Failure reason statistics
   */
  async getFailureStats(hours = 24) {
    try {
      return await APIService.get(
        `${this.endpoint}/failure-stats?hours=${hours}`
      );
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching failure stats:",
        error
      );
      throw error;
    }
  }

  /**
   * Get security health check status
   * @returns {Promise<Object>} Security health status
   */
  async getHealthCheck() {
    try {
      return await APIService.get(`${this.endpoint}/health-check`);
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching health check:",
        error
      );
      throw error;
    }
  }

  /**
   * Trigger manual cleanup of old records
   * @returns {Promise<Object>} Cleanup result
   */
  async triggerCleanup() {
    try {
      return await APIService.post(`${this.endpoint}/cleanup`, {});
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error triggering cleanup:",
        error
      );
      throw error;
    }
  }

  /**
   * Search failed attempts with flexible criteria
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.email - Email to search for
   * @param {string} searchParams.ipAddress - IP address to search for
   * @param {string} searchParams.failureReason - Failure reason to filter by
   * @param {number} searchParams.minThreatScore - Minimum threat score
   * @param {string} searchParams.startDate - Start date (ISO string)
   * @param {string} searchParams.endDate - End date (ISO string)
   * @param {number} searchParams.page - Page number
   * @param {number} searchParams.size - Page size
   * @returns {Promise<Object>} Search results
   */
  async searchAttempts(searchParams) {
    try {
      const params = new URLSearchParams();

      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params.append(key, value.toString());
        }
      });

      return await APIService.get(`${this.endpoint}/search?${params}`);
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error searching attempts:",
        error
      );
      throw error;
    }
  }

  /**
   * Export failed attempts data
   * @param {Object} exportParams - Export parameters
   * @param {string} exportParams.format - Export format ('csv', 'json')
   * @param {number} exportParams.hours - Time range in hours
   * @param {string} exportParams.email - Email filter
   * @param {string} exportParams.ipAddress - IP filter
   * @returns {Promise<Blob>} Export data as blob
   */
  async exportData(exportParams = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(exportParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          params.append(key, value.toString());
        }
      });

      // For export (blob response), we need to use fetch directly since APIService expects JSON
      const authToken = localStorage.getItem("authToken");

      const response = await fetch(
        `${API_BASE_URL}${this.endpoint}/export?${params}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: authToken ? `Bearer ${authToken}` : "",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error exporting data:",
        error
      );
      throw error;
    }
  }

  /**
   * Get real-time threat alerts
   * @returns {Promise<Object>} Current threat alerts
   */
  async getThreatAlerts() {
    try {
      return await APIService.get(`${this.endpoint}/threat-alerts`);
    } catch (error) {
      console.error(
        "[AuthenticationFailedAttemptsAPI] Error fetching threat alerts:",
        error
      );
      throw error;
    }
  }
}

// Create and export a singleton instance
const authenticationFailedAttemptsAPI = new AuthenticationFailedAttemptsAPI();
export default authenticationFailedAttemptsAPI;
